import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, formatCurrency, getCycleDateRange } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Clock,
  Plus,
  CalendarIcon,
  Download,
  Upload,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { format } from "date-fns";
import { toast } from "sonner";

// Summary Card Component
const SummaryCard = ({ title, value, icon: Icon, colorClass, bgClass, borderClass, onClick }) => (
  <Card 
    className={`summary-card card-hover cursor-pointer ${bgClass} ${borderClass}`} 
    data-testid={`summary-card-${title.toLowerCase().replace(/\s/g, '-')}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
          {title}
        </p>
        <p className={`font-body font-bold text-2xl md:text-3xl tracking-tighter ${colorClass}`}>
          {value}
        </p>
      </div>
      <div className={`p-3 rounded-xl ${bgClass}`}>
        <Icon className={`w-5 h-5 ${colorClass}`} />
      </div>
    </div>
    <p className="text-xs text-stone-400 mt-3">Click to view details →</p>
  </Card>
);

// Custom Tooltip for Chart
const CustomTooltip = ({ active, payload, label, cycleConfig }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload || {};
    const cycleRange = data.cycleKey ? getCycleDateRange(data.cycleKey, cycleConfig) : null;
    
    return (
      <div className="bg-white border border-stone-200 shadow-lg rounded-xl p-4 min-w-[220px]" data-testid="chart-tooltip">
        <p className="font-semibold text-stone-900 mb-3 pb-2 border-b border-stone-100">
          {cycleRange?.label || label}
        </p>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-stone-500">Income</span>
            <span className="font-semibold text-emerald-700">{formatCurrency(data.totalIncome || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">Expenses</span>
            <span className="font-semibold text-rose-700">{formatCurrency(data.totalExpenses || 0)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-stone-100">
            <span className="text-stone-500">Net Balance</span>
            <span className={`font-semibold ${(data.netBalance || 0) >= 0 ? 'text-indigo-700' : 'text-rose-700'}`}>
              {formatCurrency(data.netBalance || 0)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">Left to Pay</span>
            <span className="font-semibold text-amber-700">{formatCurrency(data.leftToPay || 0)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">After Bills</span>
            <span className={`font-semibold ${(data.balanceAfterBills || 0) >= 0 ? 'text-indigo-700' : 'text-rose-700'}`}>
              {formatCurrency(data.balanceAfterBills || 0)}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Add Transaction Dialog
const AddTransactionDialog = ({ type, onSuccess }) => {
  const { categories, selectedCycle, addTransaction } = useApp();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const filteredCategories = categories.filter(c => c.type === type);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!categoryId || !amount) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      addTransaction({
        dateISO: format(date, "yyyy-MM-dd"),
        type,
        categoryId,
        amount: parseFloat(amount),
        notes,
      });
      toast.success(`${type === 'income' ? 'Income' : 'Expense'} added!`);
      setOpen(false);
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Failed to add transaction");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setDate(new Date());
    setCategoryId("");
    setAmount("");
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className={`rounded-full font-medium shadow-sm active:scale-95 transition-all ${
            type === 'income'
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-rose-600 hover:bg-rose-700 text-white'
          }`}
          data-testid={`add-${type}-btn`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add {type === 'income' ? 'Income' : 'Expense'}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]" data-testid={`add-${type}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            Add {type === 'income' ? 'Income' : 'Expense'}
          </DialogTitle>
          <DialogDescription>
            Enter the details for your {type} transaction.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {filteredCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Amount (£)</Label>
            <Input
              type="number"
              placeholder="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea placeholder="Add a note..." value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <Button type="submit" className="w-full rounded-full bg-stone-900 hover:bg-stone-800" disabled={loading}>
            {loading ? "Adding..." : `Add ${type === 'income' ? 'Income' : 'Expense'}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Add Bill Dialog
const AddBillDialog = ({ onSuccess }) => {
  const { selectedCycle, addBill, categories } = useApp();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const expenseCategories = categories.filter(c => c.type === 'expense');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !amount) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      addBill({
        cycleKey: selectedCycle,
        name,
        categoryId: categoryId || null,
        amount: parseFloat(amount),
        isPaid: false,
        dueDateISO: dueDate ? format(dueDate, "yyyy-MM-dd") : null,
      });
      toast.success("Bill added!");
      setOpen(false);
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Failed to add bill");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName("");
    setCategoryId("");
    setAmount("");
    setDueDate(null);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full font-medium shadow-sm active:scale-95 transition-all bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-bill-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Bill
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]" data-testid="add-bill-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Add Bill</DialogTitle>
          <DialogDescription>Add a planned bill for this cycle.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Bill Name</Label>
            <Input placeholder="e.g., Rent, Internet" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>

          <div className="space-y-2">
            <Label>Category (optional)</Label>
            <Select value={categoryId || "none"} onValueChange={(val) => setCategoryId(val === "none" ? "" : val)}>
              <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {expenseCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Amount (£)</Label>
            <Input type="number" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} min="0" step="0.01" required />
          </div>

          <div className="space-y-2">
            <Label>Due Date (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          <Button type="submit" className="w-full rounded-full bg-stone-900 hover:bg-stone-800" disabled={loading}>
            {loading ? "Adding..." : "Add Bill"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { 
    currentSummary, 
    allSummaries, 
    selectedCycle, 
    cycleConfig,
    loading, 
    categories,
    exportData,
    importData,
    getCycleDateRange
  } = useApp();

  const chartData = [...allSummaries].reverse();
  const hasData = allSummaries.length > 0 && (currentSummary?.totalIncome > 0 || currentSummary?.totalExpenses > 0);
  const hasCategories = categories.length > 0;
  const currentCycleRange = selectedCycle ? getCycleDateRange(selectedCycle) : null;
  
  // Today's date display
  const today = new Date();
  const todayFormatted = format(today, "EEEE, MMMM d, yyyy");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="dashboard-loading">
        <div className="text-stone-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl text-stone-900" data-testid="dashboard-title">
            {currentCycleRange?.label || 'Dashboard'}
          </h2>
          <p className="text-stone-500 mt-1">Your financial overview</p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={exportData} variant="outline" size="icon" className="rounded-full hidden md:flex" data-testid="export-btn">
            <Download className="w-4 h-4" />
          </Button>
          <Button onClick={importData} variant="outline" size="icon" className="rounded-full hidden md:flex" data-testid="import-btn">
            <Upload className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Today's Date */}
      <Card className="p-3 bg-stone-100 rounded-xl border border-stone-200">
        <p className="text-sm text-stone-600 text-center">
          📅 Today: <strong>{todayFormatted}</strong>
        </p>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
        <SummaryCard
          title="Total Income"
          value={formatCurrency(currentSummary?.totalIncome || 0)}
          icon={TrendingUp}
          colorClass="text-emerald-700"
          bgClass="bg-emerald-50"
          borderClass="border-emerald-100"
          onClick={() => navigate('/income')}
        />
        <SummaryCard
          title="Total Expenses"
          value={formatCurrency(currentSummary?.totalExpenses || 0)}
          icon={TrendingDown}
          colorClass="text-rose-700"
          bgClass="bg-rose-50"
          borderClass="border-rose-100"
          onClick={() => navigate('/expenses')}
        />
        <SummaryCard
          title="Net Balance"
          value={formatCurrency(currentSummary?.netBalance || 0)}
          icon={Wallet}
          colorClass="text-indigo-700"
          bgClass="bg-indigo-50"
          borderClass="border-indigo-100"
          onClick={() => navigate('/analytics')}
        />
        <SummaryCard
          title="Left to Pay"
          value={formatCurrency(currentSummary?.leftToPay || 0)}
          icon={Clock}
          colorClass="text-amber-700"
          bgClass="bg-amber-50"
          borderClass="border-amber-100"
          onClick={() => navigate('/bills')}
        />
      </div>

      {/* Quick Actions */}
      {hasCategories && (
        <div className="flex flex-col sm:flex-row flex-wrap gap-3" data-testid="quick-actions">
          <AddTransactionDialog type="income" />
          <AddTransactionDialog type="expense" />
          <AddBillDialog />
        </div>
      )}

      {!hasCategories && (
        <Card className="p-6 bg-amber-50 border-amber-200" data-testid="no-categories-warning">
          <p className="text-amber-700">
            Please add categories first before adding transactions or bills.{" "}
            <a href="/categories" className="underline font-semibold">Go to Categories</a>
          </p>
        </Card>
      )}

      {/* Chart */}
      <Card className="p-6 bg-white rounded-2xl border border-stone-200/60 shadow-sm" data-testid="chart-card">
        <h3 className="font-body font-bold text-xl text-stone-900 mb-6">Income vs Expenses</h3>
        {chartData.length > 0 ? (
          <div className="h-[350px]" data-testid="finance-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#e7e5e4" />
                <XAxis
                  dataKey="cycleKey"
                  tick={{ fill: '#a8a29e', fontSize: 12 }}
                  tickFormatter={(value) => {
                    const range = getCycleDateRange(value);
                    return range.label.split(' → ')[0];
                  }}
                />
                <YAxis tick={{ fill: '#a8a29e', fontSize: 12 }} tickFormatter={(value) => `£${value}`} />
                <Tooltip content={<CustomTooltip cycleConfig={cycleConfig} />} />
                <Legend
                  wrapperStyle={{ paddingTop: 20 }}
                  formatter={(value) => (
                    <span className="text-stone-600 text-sm">
                      {value === 'totalIncome' ? 'Income' : 'Expenses'}
                    </span>
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="totalIncome"
                  stroke="#059669"
                  strokeWidth={2}
                  dot={{ fill: '#059669', strokeWidth: 0, r: 4 }}
                  activeDot={{ r: 6, stroke: '#059669', strokeWidth: 2, fill: '#fff' }}
                  name="totalIncome"
                />
                <Line
                  type="monotone"
                  dataKey="totalExpenses"
                  stroke="#e11d48"
                  strokeWidth={2}
                  dot={{ fill: '#e11d48', strokeWidth: 0, r: 4 }}
                  activeDot={{ r: 6, stroke: '#e11d48', strokeWidth: 2, fill: '#fff' }}
                  name="totalExpenses"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-[300px] text-stone-400" data-testid="chart-empty">
            <p>No data to display yet</p>
            <p className="text-sm">Add some transactions to see the chart</p>
          </div>
        )}
      </Card>
    </div>
  );
}
